import React from "react";
import Button from "./ui/Button";
import { useT } from "../contexts/TranslationContext";
import { usePhotoUpload } from "../contexts/PhotoUploadContext";

function PhotoUploadTray() {
  const { t } = useT();
  const { jobs, summary, retryPhoto, retryFailedPhotos, dismissCompleted } =
    usePhotoUpload();

  if (summary.total === 0) return null;

  const failedJobs = jobs.filter((job) => job.status === "failed");
  const allDone = summary.ready === summary.total;
  const percent = summary.total
    ? Math.round((summary.ready / summary.total) * 100)
    : 0;

  return (
    <>
      <div className="h-36 shrink-0" aria-hidden />
      <div
        className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none"
        role="status"
        aria-live="polite"
      >
        <div className="pointer-events-auto mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800">
                {t("report.reportSavedKeepOpen")}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {allDone
                  ? t("report.photoTrayAllDone", { count: summary.ready })
                  : summary.failed > 0 && summary.active === 0
                  ? t("report.photoTrayFailed", { count: summary.failed })
                  : t("report.uploadingPhotoProgress", {
                      current: Math.max(summary.current, 1),
                      total: summary.total,
                    })}
              </p>
            </div>
            {allDone && (
              <button
                type="button"
                onClick={dismissCompleted}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                aria-label={t("report.close")}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {!allDone && (
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
          )}

          {summary.active > 0 && (
            <p className="text-xs text-gray-500 mt-2">{t("report.pleaseWaitPhotos")}</p>
          )}

          {failedJobs.length > 0 && (
            <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto">
              {failedJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="truncate text-red-700" title={job.name}>
                    {job.name}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => retryPhoto(job.id)}
                  >
                    {t("report.photoTrayRetry")}
                  </Button>
                </div>
              ))}
              {failedJobs.length > 1 && (
                <div className="pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={retryFailedPhotos}
                  >
                    {t("report.photoTrayRetryAll")}
                  </Button>
                </div>
              )}
              {summary.active === 0 && (
                <p className="text-xs text-gray-500 pt-1">
                  {t("report.photosPartialFailure", { failed: summary.failed })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PhotoUploadTray;
